import { createSelector } from "reselect";
import groupBy from "lodash/groupBy";
import omit from "lodash/omit";

const entityIconsState = (state) => state.globalData.entityIcons;

export const getEntityGroupKeys = createSelector(entityIconsState, (data) => (type, group, includeGeneral = false) => {
	// Group the data based on the provided group key
	const groupedData = data[type] ? groupBy(data[type], group) : {};

	// Remove empty keys and get the remaining keys as an array
	const keys = Object.keys(omit(groupedData, ""));

	// Include "General" at the top if includeGeneral is true
	if (includeGeneral) {
		return ["General", ...keys];
	}

	return keys;
});

export const getEntityIconsByGroup = createSelector(
	entityIconsState,
	(data) =>
		(type, group, searchString = "", targetGroup = "") => {
			// Group the data based on the provided group key
			const groupedData = groupBy(data[type], group);
			const cleanedGroupedData = omit(groupedData, ""); // Remove empty keys

			// Handle targetGroup logic
			let filteredGroupedData;
			if (!targetGroup || targetGroup.toLowerCase() === "general") {
				// If targetGroup is empty or "General", include all data
				filteredGroupedData = cleanedGroupedData;
			} else {
				// Otherwise, filter for the specific group
				filteredGroupedData = { [targetGroup]: cleanedGroupedData[targetGroup] || [] };
			}

			// Filter and sort keys alphabetically based on the search string
			const matchingGroups = Object.keys(filteredGroupedData)
				.filter((key) => key.toLowerCase().includes(searchString.toLowerCase()))
				.sort((a, b) => a.localeCompare(b));

			// Build the result with sorted matching keys and their children
			const result = matchingGroups.reduce((acc, key) => {
				acc[key] = filteredGroupedData[key];
				return acc;
			}, {});

			return result;
		}
);

export const getEntityIconsByGroupForPoints = createSelector(entityIconsState, (data) => (type, group) => {
	const filteredIcons = Object.values(data[type])?.filter((item) => item.forPoints);
	const groupedData = groupBy(filteredIcons, (item) => (item[group] && item[group] !== "" ? item[group] : "default"));

	// Icon groups are sorted by a default grouping order initially. Additional icon groups are sorted alphabetically followed after default groups. Default icon group should remain at the end of list.
	const defaultOrder = [
		"mapMarkers",
		"incidents",
		"alarms",
		"vehicles",
		"weather",
		"alphabet",
		"sensorsAndDevices",
		"accessControl"
	];
	const additionalKeys = Object.keys(groupedData)
		?.filter((group) => !defaultOrder.includes(group) && group !== "default" && group !== "incidentSymbol") // Ignoring incident symbols that are not required for basic icons.
		.sort();
	const groupKeys = defaultOrder.concat([...additionalKeys, "default"]);

	const sortedGroupedData = groupKeys.reduce((obj, key) => {
		if (groupedData[key]) obj[key] = groupedData[key];
		return obj;
	}, {});

	return sortedGroupedData;
});

export const getEntityIconsByType = createSelector(entityIconsState, (data) => (type) => {
	return data[type];
});

export const getEntityFrameIconsByGroupName = createSelector(entityIconsState, (entityIcons) => (group) => {
	const { frame_icons } = entityIcons;
	const getObjectsByGroup = Object.values(frame_icons)
		.filter((item) => item.group === group)
		.map((item) => item.id);
	return getObjectsByGroup;
});
